import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import { 
  ArrowForward, 
  Schedule,
  Phone,
  WhatsApp
} from '@mui/icons-material';
import { services } from './data/services';
import { Link, useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();
  const [visibleElements, setVisibleElements] = useState(new Set());
  const observerRefs = useRef([]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        const element = entry.target;
        if (entry.isIntersecting) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0) scale(1)';
          element.style.filter = 'blur(0)';
          setVisibleElements(prev => new Set([...prev, element.dataset.animationId]));
        } else {
          const rect = element.getBoundingClientRect();
          const scrollingDown = rect.top > window.innerHeight;
          
          if (scrollingDown) {
            element.style.transform = 'translateY(50px) scale(0.95)';
          } else {
            element.style.transform = 'translateY(-50px) scale(0.95)';
          }
          element.style.opacity = '0';
          element.style.filter = 'blur(4px)';
          
          setVisibleElements(prev => {
            const newSet = new Set(prev);
            newSet.delete(element.dataset.animationId);
            return newSet;
          });
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '-50px',
      threshold: 0.1
    });

    observerRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.dataset.animationId = `service-${index}`;
        observer.observe(ref);
      }
    });

    return () => observer.disconnect();
  }, []);

  const getAnimationStyles = (index) => ({
    opacity: 0,
    transform: 'translateY(50px) scale(0.95)',
    filter: 'blur(4px)',
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDelay: `${index * 0.15}s`,
    willChange: 'transform, opacity, filter',
  });

  const globalStyles = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(50px) scale(0.95);
        filter: blur(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
        filter: blur(0);
      }
    }
  `;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, rgba(0,150,136,0.05) 0%, rgba(255,255,255,1) 100%)',
      pt: { xs: 8, md: 12 },
      pb: { xs: 8, md: 12 }
    }}>
      <style>{globalStyles}</style>
      
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              background: 'linear-gradient(45deg, #009688, #4CAF50)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 3,
              opacity: 0,
              transform: 'translateY(30px)',
              animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Our Comprehensive Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              opacity: 0,
              transform: 'translateY(20px)',
              animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards',
              lineHeight: 1.8,
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}
          >
            Experience personalized care with our range of specialized physiotherapy services
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={service.title}>
              <Box
                ref={el => observerRefs.current[index] = el}
                sx={{
                  ...getAnimationStyles(index),
                  '&.fade-in': {
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                    animationDelay: `${index * 0.15}s`
                  }
                }}
                className={visibleElements.has(`service-${index}`) ? 'fade-in' : ''}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    border: '1px solid rgba(0, 150, 136, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(45deg, #009688, #4CAF50)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 40px rgba(0, 150, 136, 0.15)',
                      '&::before': {
                        opacity: 1
                      },
                      '& .service-image': {
                        transform: 'scale(1.1)',
                      },
                      '& .service-content': {
                        transform: 'translateY(-5px)'
                      }
                    }
                  }}
                >
                  <Box
                    className="service-image"
                    sx={{
                      position: 'relative',
                      paddingTop: '60%',
                      overflow: 'hidden',
                      borderRadius: '20px 20px 0 0',
                      transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Box
                      component="img"
                      src={service.image}
                      alt={service.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                  </Box>
                  <CardContent
                    className="service-content"
                    sx={{
                      p: 3,
                      transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: '#009688',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        mb: 3,
                        lineHeight: 1.7
                      }}
                    >
                      {service.description}
                    </Typography>
                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        component={Link}
                        to="/book-appointment"
                        state={{ selectedService: service.title }}
                        startIcon={<Schedule />}
                        sx={{
                          background: 'linear-gradient(45deg, #009688, #4CAF50)',
                          color: 'white',
                          py: 1.5,
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(45deg, #00897B, #43A047)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 150, 136, 0.3)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Book Appointment
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        component={Link}
                        to="/contact-us"
                        state={{ fromService: service.title }}
                        endIcon={<ArrowForward />}
                        sx={{
                          color: '#009688',
                          borderColor: '#009688',
                          py: 1.5,
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 600,
                          borderWidth: '1.5px',
                          '&:hover': {
                            borderColor: '#00897B',
                            background: 'rgba(0, 150, 136, 0.05)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 150, 136, 0.1)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Contact for more details
                      </Button>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mt: 1,
                        justifyContent: 'center'
                      }}>
                        <Tooltip title="Contact via WhatsApp" arrow>
                          <IconButton
                            component={Link}
                            to="/contact-us"
                            state={{ 
                              fromService: service.title,
                              contactMethod: 'whatsapp'
                            }}
                            sx={{
                              color: '#25D366',
                              '&:hover': {
                                background: 'rgba(37, 211, 102, 0.1)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.2)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <WhatsApp />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Contact Us" arrow>
                          <IconButton
                            component={Link}
                            to="/contact-us"
                            state={{ 
                              fromService: service.title,
                              contactMethod: 'phone'
                            }}
                            sx={{
                              color: '#009688',
                              '&:hover': {
                                background: 'rgba(0, 150, 136, 0.1)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 150, 136, 0.2)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <Phone />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services; 